/* ============================================================
   Dathent — Dashboard (new, pack-aligned)
   Replaces previous Dashboard. Real-data driven: no fabricated
   counters per HANDOFF §2.1. Scenarios (active/calm/firstrun)
   derived from useDashboardData + ContentFeedContext.activeBatches.
   ============================================================ */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardTour } from '../components/DashboardTour.jsx';
import { DashboardTimezone } from '../components/TimezoneSettings.jsx';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useDashboardData } from '../state/DashboardDataContext.jsx';
import { useGenerationStatus } from '../state/GenerationStatusContext.jsx';
import { useContentFeedContext } from '../state/ContentFeedContext.jsx';
import { api } from '../services/api/client.js';
import { finishSetupState, isPostingPlan } from '../lib/finishSetup.js';
import { readBareWorkspaceHiddenAt, writeBareWorkspaceHiddenAt } from '../lib/finishSetupDismiss.js';
import { onboardingJustCompleted } from '../lib/onboardingCompletion.js';
import { usePendingOAuth } from '../contexts/PendingOAuthContext.jsx';
import { isChatGptOrigin } from '../lib/signupSource.js';
import { trialingAutopilotPlan } from '../lib/autopilotTrial.js';
import { isDashboardHydrated } from '../lib/dashboardHydration.js';
import { shouldStartDashboardTour } from '../lib/viewport.js';
import { canReadEntitledFeature } from '../lib/readAccess.js';
import { subscribeAnalyticsRefresh } from '../lib/analyticsRefresh.js';
import { targetStatusToUi } from '../lib/postStatus.js';
import { scheduledNext7Days } from '../lib/upcomingSchedule.js';
import FreeLimitsBanner from '../components/FreeLimitsBanner.jsx';
import AutopilotLimitBanner from '../components/AutopilotLimitBanner.jsx';
import TikTokStatisticsBanner from '../components/metrics/TikTokStatisticsBanner.jsx';
import { Icons as I } from '../components/pack-icons';
import {
  AttentionPanel,
  AutopilotWidget,
  EngagementChart,
  AnalyticsPanel,
  ActivationProgressCard,
  FinishSetupCard,
  FirstRunHero,
  Greeting,
  SetupJourneyWidget,
  AiSetupWidget,
  StatCards,
  WeekStrip,
  SchedulerAccountsCard,
  SchedulerRecentPosts,
} from '../components/dashboard';
import { freePublishingState } from '../lib/freePublishing.js';
import { effectiveAccessState } from '../lib/billingState.js';
import { usePersonalPublishingStart } from '../hooks/usePersonalPublishingStart.js';
import FreePublishingAllowance from '../components/freePublishing/FreePublishingAllowance.jsx';
import { useFreePreview } from '../contexts/FreePreviewContext.jsx';
import FreePreviewBoard from '../components/freePreview/FreePreviewBoard.jsx';
import FirstPostExperience from '../components/freePreview/FirstPostExperience.jsx';
import { isFirstPostOnboarding } from '../lib/freeOnboarding.js';
import '../styles/free-publishing.css';

const PLATFORM_FROM_NAME = (raw) => {
  if (!raw) return null;
  const v = String(raw).toLowerCase();
  if (v.includes('insta')) return 'instagram';
  if (v.includes('face')) return 'facebook';
  if (v.includes('linke')) return 'linkedin';
  if (v === 'x' || v.includes('twit')) return 'x';
  if (v.includes('tik')) return 'tiktok';
  if (v.includes('thread')) return 'threads';
  if (v.includes('tele')) return 'telegram';
  if (v.includes('you')) return 'youtube';
  return v;
};

function startOfWeek(d = new Date()) {
  const day = d.getDay(); // 0=Sun
  const diff = day === 0 ? -6 : 1 - day; // Monday-start
  const s = new Date(d);
  s.setDate(d.getDate() + diff);
  s.setHours(0, 0, 0, 0);
  return s;
}

function buildWeekDays(scheduleItems = [], calendarItems = [], t) {
  const monday = startOfWeek();
  const dayLabels = [
    t('dashboard.weekdayMon'),
    t('dashboard.weekdayTue'),
    t('dashboard.weekdayWed'),
    t('dashboard.weekdayThu'),
    t('dashboard.weekdayFri'),
    t('dashboard.weekdaySat'),
    t('dashboard.weekdaySun'),
  ];
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const days = dayLabels.map((label, i) => {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    return {
      label,
      date: `${date.toLocaleString('en', { month: 'short' })} ${date.getDate()}`,
      today: date.getTime() === today.getTime(),
      _date: date,
      slots: [],
    };
  });

  // We dedupe slots two ways:
  //   1. (idx, platform, time) — same minute → first one wins (rare; e.g.
  //      multi-target post duplicated in feed)
  //   2. (contentId, platform) — for the same content_item × platform,
  //      keep ONLY the best slot. Two scheduled_posts can exist for one
  //      content_item × platform (the original failed one + a cloned
  //      republish that succeeded). Without per-(content, platform)
  //      dedup the calendar shows both — the OLD failed one at its
  //      original time AND the new published one. Confusing.
  const slotKey = (idx, platform, time) => `${idx}|${platform}|${time}`;
  const seen = new Set();
  // contentPlatformBest tracks the best status we've seen so far for
  // each (contentId, platform). When we see a new candidate, we either:
  //   - replace the existing slot if the new one is better,
  //   - or skip if the existing one is already at least as good.
  // statusRank: higher = better (published > publishing > scheduled > pending > failed).
  const statusRank = {
    published: 100, done: 100,
    publishing: 60, live: 60,
    queued: 50, scheduled: 50, planned: 50, pending: 50,
    needs_manual_action: 95,
    failed: 10, needs_reconnect: 10, cancelled: 5,
  };
  const contentPlatformBest = new Map(); // key → { dayIdx, slotIdx, rank }

  // 1. Calendar items (scheduled_posts with targets) — richer: contains all statuses
  for (const item of calendarItems) {
    const post = item?.post || item;
    const when = post?.scheduledAt || post?.scheduled_at;
    if (!when) continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const dayDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const idx = days.findIndex((x) => x._date.toDateString() === dayDate.toDateString());
    if (idx === -1) continue;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const time = `${hh}:${mm}`;
    // contentItemId surfaces under slightly different keys depending on
    // which endpoint feeds us (scheduler vs calendar). Try them in order.
    const contentItemId = post?.contentItemId || post?.content_item_id || item?.contentItemId || item?.content_item_id || null;
    const targets = item?.targets || [];
    const considerSlot = (platform, status, rawStatus, targetId) => {
      const key = slotKey(idx, platform, time);
      if (seen.has(key)) return;
      // Per-(content, platform) dedup: if we already kept a slot for
      // this content+platform pair, replace it only if the new slot
      // has a better status. Avoids leaving an old "failed" entry
      // alive when the same post has since been republished.
      const cpKey = contentItemId ? `${contentItemId}|${platform}` : null;
      const newRank = statusRank[rawStatus] ?? statusRank[status] ?? 0;
      if (cpKey) {
        const prev = contentPlatformBest.get(cpKey);
        if (prev && prev.rank >= newRank) {
          // Existing slot is at least as good — keep it, skip this one.
          return;
        }
        if (prev) {
          // Remove the worse existing slot.
          const prevDay = days[prev.dayIdx];
          if (prevDay) prevDay.slots = prevDay.slots.filter((_, i) => i !== prev.slotIdx);
        }
      }
      seen.add(key);
      days[idx].slots.push({ platform, status, time, contentItemId, targetId });
      if (cpKey) {
        contentPlatformBest.set(cpKey, {
          dayIdx: idx,
          slotIdx: days[idx].slots.length - 1,
          rank: newRank,
        });
      }
    };

    if (targets.length === 0) {
      // No targets yet — single slot keyed off post status
      const platform = PLATFORM_FROM_NAME(post.platform);
      const rawStatus = post.status;
      const status = rawStatus === 'published' ? 'done' : 'scheduled';
      considerSlot(platform, status, rawStatus, null);
    } else {
      for (const t of targets) {
        const platform = PLATFORM_FROM_NAME(t.platform);
        const tStatus = t.status;
        const status = tStatus === 'published'
          ? 'done'
          : tStatus === 'needs_manual_action'
            ? targetStatusToUi(tStatus, { platform, errorCode: t.lastErrorCode })
          : tStatus === 'failed' || tStatus === 'needs_reconnect'
            ? 'failed'
            : (tStatus === 'live' || (Math.abs(d - new Date()) < 3600000 && d <= new Date()))
              ? 'live'
              : 'scheduled';
        considerSlot(platform, status, tStatus, t?.id || null);
      }
    }
  }

  // 2. Fallback to schedule items (upcoming/planned) — merges with calendar if not seen
  for (const item of scheduleItems) {
    const when = item?.scheduledAt || item?.scheduled_at || item?.publishAt || item?.publish_at;
    if (!when) continue;
    const d = new Date(when);
    if (Number.isNaN(d.getTime())) continue;
    const dayDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const idx = days.findIndex((x) => x._date.toDateString() === dayDate.toDateString());
    if (idx === -1) continue;
    const platform = PLATFORM_FROM_NAME(item.platform || item.platformId || item.platform_name);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const time = `${hh}:${mm}`;
    const key = slotKey(idx, platform, time);
    if (seen.has(key)) continue;
    seen.add(key);
    const itemStatus = item.status || item.post?.status;
    const status = itemStatus === 'published'
      ? 'done'
      : (itemStatus === 'live' || (Math.abs(d - new Date()) < 3600000 && d <= new Date()))
        ? 'live'
        : 'scheduled';
    const contentItemId = item?.contentItemId || item?.content_item_id || item?.post?.contentItemId || item?.post?.content_item_id || null;
    days[idx].slots.push({ platform, status, time, contentItemId, targetId: item?.targetId || item?.target_id || null });
  }

  // Sort slots within each day by time
  for (const day of days) {
    day.slots.sort((a, b) => (a.time || '').localeCompare(b.time || ''));
  }

  return days.map(({ _date, ...rest }) => rest);
}

function buildChecklist(state, t) {
  const { socialAccounts } = state;
  const steps = [
    {
      label: t('dashboard.checklistConnectLabel'),
      sub: socialAccounts?.length
        ? t('dashboard.checklistConnectSub', { count: socialAccounts.length })
        : t('dashboard.checklistConnectSubEmpty'),
      done: (socialAccounts?.length || 0) > 0,
      cta: t('dashboard.checklistConnectCta'),
      to: '/app/settings?tab=social',
    },
  ];
  steps.push({
    label: t('dashboard.checklistGenerateLabel'),
    sub: t('dashboard.checklistGenerateSub'),
    done: false,
    cta: t('dashboard.checklistGenerateCta'),
    to: '/app/create',
  });
  return steps;
}

function deriveAttentionItems(state, navigate, t, capability = null) {
  const items = [];
  const accounts = state.socialAccounts || [];

  // 1. No accounts connected → suggest connecting before generation hits a wall
  if (accounts.length === 0) {
    items.push({
      kind: 'info',
      title: t('dashboard.attentionNoAccountsTitle'),
      sub: t('dashboard.attentionNoAccountsSub'),
      action: t('dashboard.attentionConnectAction'),
      onAction: () => navigate('/app/settings?tab=accounts'),
    });
  } else {
    // 2. Suggest broadening reach when very few platforms are linked
    const platforms = new Set(accounts.map((a) => String(a.platform || '').toLowerCase()).filter(Boolean));
    const connectedCount = accounts.filter(account => !['disconnected', 'revoked'].includes(String(account?.status || '').toLowerCase())).length;
    if (platforms.size === 1 && (!capability || connectedCount < capability.limits.socialAccounts)) {
      items.push({
        kind: 'info',
        title: t('dashboard.attentionReachTitle'),
        sub: t('dashboard.attentionReachSub'),
        action: t('dashboard.attentionAddPlatformAction'),
        onAction: () => navigate('/app/settings?tab=accounts'),
      });
    }
  }

  // 3. Accounts that explicitly need reconnect (revoked / token expired / needs_manual_action)
  const needsReconnect = accounts.filter((a) => {
    const s = String(a?.status || '').toLowerCase();
    return s === 'needs_reconnect' || s === 'needs_manual_action' || s === 'expired' || s === 'token_expired';
  });
  for (const acc of needsReconnect) {
    items.push({
      kind: 'warn',
      title: t('dashboard.attentionReconnectTitle', { account: acc.displayName || acc.platform || t('dashboard.accountFallback') }),
      sub: t('dashboard.attentionReconnectSub'),
      action: t('dashboard.attentionReconnectAction'),
      onAction: () => navigate('/app/settings?tab=accounts'),
    });
  }

  // 4. Tokens expiring soon (within 7 days)
  const expiring = accounts.filter((a) => {
    const expiresAt = a?.tokenExpiresAt || a?.token_expires_at;
    if (!expiresAt) return false;
    const days = (new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24);
    return days > 0 && days < 7;
  });
  for (const acc of expiring) {
    items.push({
      kind: 'warn',
      title: t('dashboard.attentionExpiringTitle', { account: acc.platform || t('dashboard.accountFallback') }),
      sub: t('dashboard.attentionExpiringSub'),
      action: t('dashboard.attentionReconnectAction'),
      onAction: () => navigate('/app/settings?tab=accounts'),
    });
  }

  return items;
}

// Free publishing uses the normal dashboard without starting AI generation.
export const Dashboard = () => {
  const preview = useFreePreview();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth();
  const dash = useDashboardData();
  const entitlements = dash.entitlementsError ? null : dash.entitlements;
  const freePublishing = freePublishingState(entitlements);
  const knownFree = useRef({ userId: user?.id, value: false });
  if (knownFree.current.userId !== user?.id) knownFree.current = { userId: user?.id, value: false };
  if (freePublishing) knownFree.current.value = true;
  const paidAccess = ['active', 'trialing', 'cancel_scheduled', 'grace', 'lapsed', 'downgrade_scheduled'].includes(effectiveAccessState(entitlements));
  // A malformed/revoked Free capability is not an AI-generation entitlement.
  // Keep this user's previous safe mode until the server confirms paid access.
  if (!freePublishing && ((knownFree.current.value && !paidAccess) || entitlements?.freePublishing?.active === true)) {
    return <div className="dash-root"><section className="card dash-setup-loading" role="alert">
      <span>{t('scheduler.loadError')}</span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => dash.invalidateResource?.('entitlements', { fresh: true })}>{t('common.retry')}</button>
    </section></div>;
  }
  const firstPostSetup = Boolean(freePublishing && preview.active && isFirstPostOnboarding(preview.entitlements));
  // Preserve the legacy preview board; the one-post setup stays inline in
  // the normal dashboard, including after email confirmation and reload.
  if (preview.active && !firstPostSetup && new URLSearchParams(location.search).get('preview') === '1') {
    return <div className="dash-root fpb-focus"><FreePreviewBoard /></div>;
  }
  return <RegularDashboard freePublishing={freePublishing} firstPostSetup={firstPostSetup} />;
};

const RegularDashboard = ({ freePublishing = null, firstPostSetup = false }) => {
  const { t } = useTranslation();
  const analyticsScope = api.getReadScope();
  const { user } = useAuth();
  const pendingOAuth = usePendingOAuth();
  // TASK (2026-09-10, ChatGPT-skip-onboarding, B4): scoped to the ChatGPT
  // cohort ONLY — a bare/minimal brand can also come from the existing
  // "Post as yourself" quick-brand action (QuickBrandSheet, available on
  // this same card today), which is a deliberate opt-out of full branding
  // for ANY account and must keep hiding the card exactly as before.
  const chatgptOrigin = isChatGptOrigin(pendingOAuth.pending, user);
  const dashState = useDashboardData();
  const free = Boolean(freePublishing);
  const personalStart = usePersonalPublishingStart(free);
  // Never mount setup/generation writers while access is unknown or failed.
  // In particular, a failed fresh Free allowance read must not fall through
  // to the paid onboarding chain.
  const accessKnown = Boolean(dashState.entitlements) && !dashState.entitlementsError;
  const allowSetupWidgets = accessKnown && !free;
  const analyticsReadable = accessKnown && (!free || canReadEntitledFeature(dashState.entitlements, 'analytics'));
  const { activeBatches, counts, content, loading: contentFeedLoading, hasLoaded: contentFeedHasLoaded, error: contentFeedError, refreshContent } = useContentFeedContext();
  const { operationsArr } = useGenerationStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const [postsSummary, setPostsSummary] = useState(null);
  const [postsSummaryLoading, setPostsSummaryLoading] = useState(false);
  const [overview, setOverview] = useState(null);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [analyticsRefresh, setAnalyticsRefresh] = useState(0);
  const [calendarItems, setCalendarItems] = useState([]);
  useEffect(() => subscribeAnalyticsRefresh((scope) => {
    if (scope === api.getReadScope()) setAnalyticsRefresh(value => value + 1);
  }), []);
  // Dashboard coachmark tour — triggered by `?tour=1` (from onboarding
  // celebrate's "Show me around" CTA). One-shot per browser via the
  // localStorage flag inside DashboardTour. We strip the query param
  // off the URL on mount so a refresh doesn't replay the tour.
  const [showTour, setShowTour] = useState(false);
  useEffect(() => {
    const hasTourParam = new URLSearchParams(location.search).has('tour');
    // Mobile shell fix: the coach-marks cover the mobile UI, so mobile never
    // starts this tour — and leaves `?tour=1` UNCONSUMED (no setShowTour,
    // no history.replaceState) so the exact same link still triggers it
    // later on desktop instead of being silently burned on mobile.
    if (!shouldStartDashboardTour(hasTourParam)) return;
    setShowTour(true);
    // Strip ?tour=1 so a page refresh doesn't replay the tour
    window.history.replaceState({}, '', location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Normal TTL-respecting fetch for everything (entitlements/schedule/
    // publishingPrefs — no need to bust those on every dashboard remount).
    dashState.ensureDashboardData?.();
    // Onboarding can leave this provider with just-invalidated setup/account
    // resources. Force ONLY these two so skip/completion is visible on first
    // paint rather than after a TTL or full-page reload, without forcing a
    // refetch of the other TTL-cached resources on every dashboard mount.
    dashState.invalidateResource?.('setupStatus');
    dashState.invalidateResource?.('socialAccounts');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Multi-metric overview (reach / platforms / interactions) for AnalyticsPanel
  useEffect(() => {
    let cancelled = false;
    setOverview(null);
    if (!analyticsReadable) return undefined;
    api.analytics
      .overview({ days: analyticsDays })
      .then((res) => { if (!cancelled && analyticsScope === api.getReadScope()) setOverview(res?.data && typeof res.data === 'object' ? res.data : res); })
      .catch(() => { if (!cancelled && analyticsScope === api.getReadScope()) setOverview(null); });
    return () => { cancelled = true; };
  }, [analyticsReadable, analyticsScope, analyticsRefresh, analyticsDays]);

  // Fetch analytics summary (published counts, avg engagement, series)
  useEffect(() => {
    let cancelled = false;
    setPostsSummaryLoading(true);
    setPostsSummary(null);
    if (!analyticsReadable) { setPostsSummaryLoading(false); return undefined; }
    api.analytics
      .getPostsSummary({ days: analyticsDays })
      .then((res) => {
        if (cancelled || analyticsScope !== api.getReadScope()) return;
        // API client may unwrap { data: ... } or return raw — handle both
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setPostsSummary(payload);
      })
      .catch(() => {
        if (cancelled || analyticsScope !== api.getReadScope()) return;
        setPostsSummary(null);
      })
      .finally(() => {
        if (!cancelled) setPostsSummaryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [analyticsReadable, analyticsScope, analyticsRefresh, analyticsDays]);

  // Fetch calendar items for current week (includes published + scheduled w/ targets),
  // reaching at least 7 days ahead: the "Scheduled · 7d" card counts this same
  // payload (no LIMIT, unlike dashState.schedule — GH-975). Re-runs whenever
  // a publish operation transitions to terminal so the
  // WeekStrip flips "failed" → "published" without waiting for the 30s
  // cache window — and bust the api.scheduler.getCalendar cache before
  // refetching so we don't get the same stale payload back.
  const publishOpTerminalSig = useMemo(() => {
    return operationsArr
      .filter((op) => op.kind === 'publish' && (op.status === 'succeeded' || op.status === 'failed'))
      .map((op) => `${op.id}:${op.status}:${op.updatedAt}`)
      .sort()
      .join('|');
  }, [operationsArr]);

  useEffect(() => {
    let cancelled = false;
    const monday = startOfWeek();
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 7);
    const nextWeek = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000);
    const to = nextWeek > sunday ? nextWeek : sunday;
    // Bust the 30s GET cache before requesting — otherwise the same
    // pre-publish snapshot keeps coming back for half a minute even
    // though publish_targets have flipped.
    try { api.invalidateCache?.('/api/scheduler/calendar'); } catch (_) {}
    api.scheduler
      .getCalendar(monday.toISOString(), to.toISOString())
      .then((res) => {
        if (cancelled) return;
        const payload = res?.data && typeof res.data === 'object' ? res.data : res;
        setCalendarItems(payload?.posts || []);
      })
      .catch(() => {
        if (!cancelled) setCalendarItems([]);
      });
    return () => {
      cancelled = true;
    };
  }, [publishOpTerminalSig]);

  const activeBatch = useMemo(() => {
    const running = (activeBatches || []).find((b) => ['queued', 'generating', 'reserved'].includes(b.status));
    return running || null;
  }, [activeBatches]);

  // Have the scenario-deciding resources actually been fetched yet? fetchedAt
  // starts at 0 for each key and flips to a timestamp once the request settles
  // (success OR error — see DashboardDataContext). Until then schedule/
  // socialAccounts are just their empty initial values, NOT a confirmed
  // "this user has nothing" state.
  const dataReady = Boolean(dashState.fetchedAt?.schedule && dashState.fetchedAt?.socialAccounts);
  const brandEngaged = (dashState.schedule || []).length > 0 || (dashState.socialAccounts || []).length > 0;

  const scenario = useMemo(() => {
    if (activeBatch) return 'active';
    const hasSchedule = (dashState.schedule || []).length > 0;
    const hasSocial = (dashState.socialAccounts || []).length > 0;
    if (hasSchedule || hasSocial) return 'calm';
    // Only fall back to the first-run template once we've LOADED and confirmed
    // the account is genuinely empty. Before that, render the real 'calm'
    // layout (Autopilot, Attention, stats, charts — just unfilled) so nothing
    // template-y flashes: the modules simply fill in, instead of FirstRunHero
    // showing for ~1s and then being swapped out (the "template flash" on
    // dashboard entry).
    if (!dataReady) return 'calm';
    return 'firstrun';
  }, [activeBatch, dashState.schedule, dashState.socialAccounts, dataReady]);

  // NOTE: Dashboard used to publish its OWN activity here, but
  // BatchActivityBridge (mounted globally inside AppShell) already does
  // exactly this — and it includes activity.batchId so clicking the top
  // pill opens the batch detail screen. Dashboard's duplicate setActivity
  // call was clobbering the bridge's value with a copy that lacked
  // batchId, leaving the pill unclickable (user could see "Batch
  // generation / Composing visuals" but no way to navigate into it,
  // and the "Open batch" button in the panel disappeared because its
  // render condition is `activity.batchId && ...`).
  // Bridge is now the single source of truth for batch activity state.

  const weekDays = useMemo(() => buildWeekDays(dashState.schedule || [], calendarItems, t), [dashState.schedule, calendarItems, t]);
  const scheduledSoon = useMemo(() => scheduledNext7Days(calendarItems), [calendarItems]);

  const stats = useMemo(() => {
    const c = counts || {};
    const out = [];
    const period = overview?.periodSummary;
    const published = typeof period?.publishedTargets === 'number' ? period.publishedTargets : postsSummary?.publishedCount;
    const windowLabel = `${analyticsDays}d`;

    // The selected analytics window takes precedence over the all-time count.
    if (typeof published === 'number') {
      out.push({
        label: `${t('analyticsPanel.postsPublished')} · ${windowLabel}`,
        value: published,
        unit: '',
        delta: !period && typeof postsSummary?.publishedDeltaPct === 'number' ? postsSummary.publishedDeltaPct : undefined,
        sub: period && published > 0 ? null : !period && postsSummary?.publishedPrevCount > 0
          ? t('dashboard.statPublishedVsPrev', { count: postsSummary.publishedPrevCount })
          : (published === 0 ? t('dashboard.statPublishedNoneRecent') : t('dashboard.statPublishedFirstWindow')),
      });
    } else {
      out.push({
        label: t('dashboard.statPublished'),
        value: c.published || 0,
        unit: t('dashboard.unitPosts'),
        sub: (c.published || 0) === 0 ? t('dashboard.statPublishedNoneYet') : t('dashboard.statPublishedTotal'),
      });
    }

    // A rate can exceed 100% when interactions exceed reported reach. If it
    // has no measured denominator, show the interaction count instead.
    const rate = period ? period.avgEngagementRate : postsSummary?.avgEngagement;
    const rateMeasured = period ? period.engagementRateMeasuredTargets > 0 : true;
    if (rateMeasured && typeof rate === 'number' && Number.isFinite(rate) && rate >= 0) {
      out.push({
        label: t('dashboard.statAvgEngagement'),
        value: rate.toFixed(1),
        unit: '%',
        sub: t('dashboard.statAvgEngagementSub'),
      });
    } else if (period ? period.interactionsMeasuredTargets > 0 && typeof period.totalInteractions === 'number' && period.totalInteractions >= 0 : typeof postsSummary?.totalEngagement === 'number' && postsSummary.totalEngagement >= 0) {
      out.push({
        label: `${t('analyticsPanel.totalInteractions')} · ${windowLabel}`,
        value: period ? period.totalInteractions : postsSummary.totalEngagement,
        unit: '',
        sub: t('dashboard.statEngagementBreakdown'),
      });
    } else {
      out.push({
        label: t('dashboard.statAvgEngagement'),
        value: '—',
        unit: '',
        sub: t('socialMetrics.unavailable'),
      });
    }

    // In pipeline (real-time)
    const inPipeline = (c.generating || 0) + (activeBatch ? Math.max(0, (activeBatch.total_items || activeBatch.totalItems || 0) - (activeBatch.ready_count || activeBatch.readyCount || 0)) : 0);
    if (inPipeline > 0 || activeBatch) {
      out.push({
        label: t('dashboard.statInPipeline'),
        value: inPipeline,
        unit: t('dashboard.unitPosts'),
        sub: activeBatch ? t('dashboard.statInPipelineGenerating') : t('dashboard.statInPipelineQueued'),
      });
    }

    // Ready to schedule
    out.push({
      label: t('dashboard.statReadyToSchedule'),
      value: c.ready || 0,
      unit: t('dashboard.unitPosts'),
      sub: (c.ready || 0) === 0 ? t('dashboard.statReadyScheduleFromLibrary') : t('dashboard.statReadyAwaitingSlot'),
    });

    // Scheduled · 7d
    out.push({
      label: t('dashboard.statScheduled7d'),
      value: scheduledSoon,
      unit: t('dashboard.unitPosts'),
      sub: scheduledSoon === 0 ? t('dashboard.statScheduledNone') : t('dashboard.statScheduledAcrossPlatforms'),
    });

    // Cap to 4 — most important first
    return out.slice(0, 4);
    // `t` MUST stay in the deps: without it, switching language leaves these
    // cards memoized in the old language until some OTHER dep (data refresh)
    // happens to change — which is exactly the stale-translation bug.
  }, [counts, scheduledSoon, activeBatch, postsSummary, overview, analyticsDays, t]);

  // Match the chart unit to measured rate coverage. Keep missing days as
  // gaps and never clamp a valid percentage to the 0–100 range.
  const engagementChart = useMemo(() => {
    const source = postsSummary?.series?.length ? postsSummary.series : overview?.engagement;
    if (!source?.length) return { data: [], mode: 'rate' };
    const hasRate = overview?.periodSummary
      ? overview.periodSummary.engagementRateMeasuredTargets > 0
      : typeof postsSummary?.avgEngagement === 'number' && Number.isFinite(postsSummary.avgEngagement);
    const mode = hasRate ? 'rate' : 'count';
    const data = source.map((row) => {
      const d = new Date(row.date);
      const label = isNaN(d.getTime())
        ? row.date
        : d.toLocaleDateString('en', { month: 'short', day: 'numeric', timeZone: 'UTC' });
      const rateValue = typeof row.engagement === 'number' ? row.engagement : null;
      return {
        date: row.date,
        label,
        engagement: mode === 'rate'
          ? (Number.isFinite(rateValue) && rateValue >= 0 ? rateValue : null)
          : (typeof row.engagementCount === 'number' && row.engagementCount >= 0 ? row.engagementCount : null),
        published: typeof row.published === 'number' && Number.isFinite(row.published) ? row.published : null,
      };
    });
    return { data, mode };
  }, [postsSummary, overview?.periodSummary, overview?.engagement]);
  const engagementSeries = engagementChart.data;
  const engagementMode = engagementChart.mode;

  const attentionItems = useMemo(() => deriveAttentionItems(dashState, navigate, t, freePublishing), [dashState, navigate, t, freePublishing]);
  // Cross-org brand-scope fix follow-up (2026-09-08): the "First post — Done"
  // row had no link to the post it actually produced. Earliest item of the
  // content feed by createdAt — the first-post generation call is the very
  // first content_item a fresh workspace ever produces, so it's also the
  // oldest by construction; falling back to createdAt-sort (rather than
  // trusting feed order, which is newest-first) keeps this correct even if
  // the feed has since grown.
  // NOTE (adversarial-review round, m3): `content` here is whatever page
  // useContentFeedContext() has loaded — the dashboard only ever fetches
  // the FIRST page, so on an org with more posts than one page this scans
  // a window, not the true full history. Acceptable: this only matters
  // for the "Finish setup" card's one-time first-post link, and any post
  // old enough to have been paginated out of the first page was reached
  // long before that card would still be showing. When `counts.all === 1`
  // (the exact case right after onboarding, when this link first matters)
  // the scan is over the complete set and the result is exact, not a guess.
  const firstPostId = useMemo(() => {
    if (!Array.isArray(content) || content.length === 0) return null;
    let earliestTs = null;
    let earliestId = null;
    for (const row of content) {
      const item = row?.contentItem || row;
      const ts = Date.parse(item?.createdAt || '');
      if (Number.isNaN(ts)) continue;
      if (earliestTs === null || ts < earliestTs) {
        earliestTs = ts;
        earliestId = item?.id || null;
      }
    }
    return earliestId;
  }, [content]);
  // TASK-214 / TASK-270: "Finish setup" card for the preview-paywall cohort
  // that reached the dashboard without a brand profile. A stale onboarding
  // session never revives the card once a brand exists. Pure decision; null =
  // hidden (setup complete, not fetched yet, or a probe failed for an
  // uninterpretable reason). onboardingJustCompleted() is the same 60 s
  // replica-lag grace RequireOnboarding applies, so the card can't flash for
  // a user who finished onboarding seconds ago.
  // TASK (2026-09-11, bare-brand-prompts #2): the bare-workspace card's own
  // "×" dismiss. Re-reads the per-account marker whenever the user id changes
  // (never stuck on a stale/undefined id from before auth resolved) or after
  // a dismiss click bumps the counter, so finishSetup recomputes immediately
  // without waiting for the next mount/focus re-fetch.
  const [bareHideBump, setBareHideBump] = useState(0);
  const bareWorkspaceHiddenAt = useMemo(
    () => readBareWorkspaceHiddenAt(user?.id),
    [user?.id, bareHideBump],
  );
  const dismissFinishSetupBare = () => {
    writeBareWorkspaceHiddenAt(user?.id);
    setBareHideBump((n) => n + 1);
  };
  // A null setup decision means either "complete" or "not loaded yet".
  // Scheduler must not show its fallback accounts card/gold Create CTA until
  // the inputs behind that decision have settled. Its first-post step also
  // depends on the separately loaded content feed, whose initial count is 0.
  const setupCardPending = !dashState.fetchedAt?.entitlements || (isPostingPlan(dashState.entitlements) && (
    !dashState.fetchedAt?.setupStatus || !dashState.fetchedAt?.socialAccounts || !contentFeedHasLoaded
  ));
  const setupProbeFailed = dashState.resourceErrors?.setupStatus?.initial || dashState.setupStatus?.brand === 'unknown';
  const accountsProbeFailed = dashState.resourceErrors?.socialAccounts?.initial;
  const contentProbeFailed = Boolean(contentFeedError) && !contentFeedHasLoaded;
  const setupCardError = isPostingPlan(dashState.entitlements) && (setupProbeFailed || accountsProbeFailed || contentProbeFailed);
  const [retryingSetup, setRetryingSetup] = useState(false);
  const retrySetup = async () => {
    if (retryingSetup) return;
    setRetryingSetup(true);
    try {
      await Promise.allSettled([
        ...(setupProbeFailed ? [dashState.invalidateResource('setupStatus')] : []),
        ...(accountsProbeFailed ? [dashState.invalidateResource('socialAccounts')] : []),
        ...(contentProbeFailed ? [refreshContent({ refreshMetrics: false })] : []),
      ]);
    } finally {
      setRetryingSetup(false);
    }
  };
  const finishSetup = useMemo(
    () => !allowSetupWidgets || setupCardPending || setupCardError ? null : finishSetupState({
      setup: dashState.setupStatus,
      socialAccounts: dashState.socialAccounts,
      postCount: counts?.all || 0,
      justCompleted: onboardingJustCompleted(),
      entitlements: dashState.entitlements,
      firstPostId,
      chatgptOrigin,
      bareWorkspaceHiddenAt,
    }),
    [allowSetupWidgets, setupCardPending, setupCardError, dashState.setupStatus, dashState.socialAccounts, dashState.entitlements, counts?.all, firstPostId, chatgptOrigin, bareWorkspaceHiddenAt],
  );
  const checklist = useMemo(() => buildChecklist(dashState, t), [dashState, t]);
  const greetingName = user?.name || user?.fullName || (user?.email ? user.email.split('@')[0] : null);
  // On a brand-new (firstrun) account we normally keep the dashboard clean and
  // hide the Autopilot panel. EXCEPTION: a Free account should still see the
  // locked Autopilot card with its Upgrade CTA — it's a key upsell surface and
  // users expect it there. Show it on firstrun only once entitlements have loaded
  // AND confirm autopilot is NOT available (Free); paid-firstrun stays clean and
  // there's no locked→hidden flash while entitlements are still loading.
  const autopilotUpsellOnFirstrun = Boolean(dashState.entitlements) && dashState.entitlements.features?.autopilot !== true;
  // TASK-331 (D6 fix): this decides whether <AutopilotWidget/> mounts at all
  // on a no-brand firstrun dashboard (line ~728 below) — same READ-only
  // re-derivation as AutopilotWidget's own internal gate, so a lapsed org
  // with subscription history doesn't lose visibility into an autopilot it
  // had already configured, in the (narrow) case its brand isn't "engaged"
  // by this screen's other heuristics.
  const autopilotEntitled = canReadEntitledFeature(dashState.entitlements, 'autopilot');
  const autopilotTrialAwaitingPayment = trialingAutopilotPlan(dashState.entitlements);
  const noBrand = dashState.setupStatus?.brand === 'missing';

  // TASK-319, revised by owner decision 2026-09-07: posting (Scheduler) plan
  // composition — analytics is now INCLUDED (StatCards + AnalyticsPanel/
  // WeekStrip render exactly like every other plan); still excluded are the
  // AI-generation widgets (Autopilot, content-plan/strategy, brand-kit
  // cross-sell). Cross-sell for the excluded AI tools lives on /app/plans
  // and the New Post header link only. Every rule below is posting-gated so
  // non-posting plans render byte-identically to before this task.
  const posting = isPostingPlan(dashState.entitlements);
  const publishingOnly = posting || free;
  const recentPosts = useMemo(() => (content || []).slice(0, 5), [content]);
  // Fix list item 8b: `posting` reads false (isPostingPlan(undefined)) for
  // one tick on every load, before entitlements have actually been fetched
  // — so every posting-gated block below would flash the NON-posting
  // composition in first. planKnown gates exactly the plan-dependent region
  // (StatCards, the dash-grid-2 composition, SchedulerRecentPosts,
  // SchedulerAccountsCard, the top-row Create-post CTA); Greeting,
  // FinishSetupCard, banners and AttentionPanel stay immediate. Trade-off:
  // non-posting plans now see that region appear one fetch later than
  // before instead of never having shown the wrong composition.
  const planKnown = accessKnown && Boolean(dashState.fetchedAt?.entitlements);
  // Owner finding (staging 2026-09-08): ActivationProgressCard reads postCount
  // straight off ContentFeedContext.counts.all, which starts at 0 (the empty
  // default) until that context's OWN initial fetch resolves — independent of
  // this screen's own dashState fetches and of the card's internal
  // profile/research/strategy `loaded` gate. While postCount reads that stale
  // 0, decideActivationProgress's only "fully activated, nothing to narrate"
  // short-circuit (hasPlan && postCount > 0) can miss even for an org that is
  // genuinely done, letting the in-progress checklist flash for a beat before
  // the real count arrives. dashboardHydrated (lib/dashboardHydration.js) gates
  // the card's own MOUNT on both entitlements and the content feed actually
  // having loaded, so it never renders off not-yet-loaded server state.
  const dashboardHydrated = isDashboardHydrated({ entitlementsFetched: planKnown, contentFeedLoading });

  // Soft "run market research" nudge under the All-clear state — for accounts
  // that CAN run research (entitled) but never have (mostly legacy users). We
  // only probe the research list when entitled, so Free accounts make no extra
  // call and never get an upsell here. Deterministic: vanishes once any report
  // exists, so it never pops up randomly.
  // TASK-331 (D6 fix): a lapsed org with subscription history keeps reading
  // its existing research report — the gateway already serves it in full.
  // This is a READ-only re-derivation: the probe below is api.research.list()
  // (a GET), and the hint's own action just navigates to /app/research, where
  // running NEW research stays correctly blocked by the PLAN_LAPSED 402.
  const researchEntitled = allowSetupWidgets && canReadEntitledFeature(dashState.entitlements, 'research');
  const [hasResearch, setHasResearch] = useState(null); // null = unknown
  useEffect(() => {
    if (!researchEntitled) return undefined;
    let alive = true;
    api.research.list()
      .then((res) => { const reports = (res?.data ?? res)?.reports || []; if (alive) setHasResearch(reports.length > 0); })
      .catch(() => {});
    return () => { alive = false; };
  }, [researchEntitled]);
  const researchHint = useMemo(() => {
    if (!researchEntitled || hasResearch !== false) return null;
    return {
      title: t('attentionPanel.researchHintTitle'),
      sub: t('attentionPanel.researchHintSub'),
      action: t('attentionPanel.researchHintCta'),
      onAction: () => navigate('/app/research'),
    };
  }, [researchEntitled, hasResearch, t, navigate]);

  // Variant A setup card (owner decision 2026-09-09): while the posting
  // FinishSetupCard is visible it owns the screen's single gold CTA — the
  // card's own active row IS the "what to do next" action, so
  // SchedulerAccountsCard's "No accounts connected yet" banner (which
  // duplicates the card's social row 1:1) stays hidden until an account
  // exists, and the top-row "Create post" stays ghost for as long as the
  // card itself is on screen (its row 2 is the gold "Create post" once
  // social is done). Both re-join today's plain posting layout the moment
  // the card disappears (both required steps done). FinishSetupCard itself
  // stays pure (lib/finishSetup.js) — socialAccounts is attached here, at
  // the render call site, only for the posting card's row-1 platform chips.
  const socialStepDone = finishSetup?.posting
    ? Boolean(finishSetup.steps.find((s) => s.key === 'social')?.done)
    : true;
  const postingSetupPending = posting && setupCardPending && !setupCardError;
  const hideSchedulerAccountsCard = postingSetupPending || setupCardError || (Boolean(finishSetup?.posting) && !socialStepDone);
  const topCreatePostGhost = postingSetupPending || setupCardError || Boolean(finishSetup?.posting);
  const finishSetupCardState = finishSetup
    ? { ...finishSetup, socialAccounts: dashState.socialAccounts || [] }
    : finishSetup;

  return (
    <div className="dash-root">
      {/* GH-939: renders only for a connected TikTok account missing the statistics scopes. */}
      <TikTokStatisticsBanner />
      <div className="dash-top-row">
        <Greeting scenario={scenario} name={greetingName} posting={publishingOnly}>
          {free && <FreePublishingAllowance capability={freePublishing} />}
        </Greeting>
        <div className="dash-top-actions">
          <DashboardTimezone />
          {planKnown && free ? (
            <button type="button" className={`btn ${pendingOAuth.personalPendingOptions?.length ? 'btn-secondary' : 'btn-primary'} btn-sm`} disabled={personalStart.disabled} onClick={() => personalStart.start('create')}>
              {t(personalStart.busy ? 'freePublishing.preparing' : 'freePublishing.newPost')} <I.arrowRight size={13} />
            </button>
          ) : planKnown && posting && (
            <Link to="/app/create" className={topCreatePostGhost ? 'btn btn-ghost btn-sm' : 'btn btn-primary btn-sm'}>
              {t('finishSetup.firstPostCta')} <I.arrowRight size={13} />
            </Link>
          )}
        </div>
      </div>
      {dashState.entitlementsError && <section className="card dash-setup-loading" role="alert">
        <span>{t('scheduler.loadError')}</span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={() => dashState.invalidateResource?.('entitlements', { fresh: true })}>{t('common.retry')}</button>
      </section>}
      {free && personalStart.error && <p role="alert">{t('freePublishing.startError')}</p>}
      {firstPostSetup && <FirstPostExperience resumeSetup={new URLSearchParams(location.search).get('preview') === '1'} />}

      {/* TASK-214: setup not finished (no brand / onboarding in_progress) —
          progress + CTA back to /onboarding. Replaces FirstRunHero for this
          cohort below: that hero assumes the brand is already configured. */}
      {planKnown && postingSetupPending && (
        <section className="card dash-setup-loading" role="status" aria-busy="true">
          {t('common.loading')}
        </section>
      )}
      {planKnown && setupCardError && (
        <section className="card dash-setup-loading" role="status" aria-busy={retryingSetup}>
          <span>{t('scheduler.loadError')}</span>
          <button type="button" className="btn btn-ghost btn-sm" disabled={retryingSetup} onClick={retrySetup}>
            {t(retryingSetup ? 'common.loading' : 'common.retry')}
          </button>
        </section>
      )}
      {finishSetup && <FinishSetupCard state={finishSetupCardState} onDismissBare={dismissFinishSetupBare} />}

      {/* TASK-319: posting-only accounts summary — the one thing missing
          from the dashboard itself for a Scheduler account (previously only
          visible inside Settings). Pure presentational, fed from data the
          dashboard already loads — no new fetch. Hidden while the setup
          card's social step isn't done yet (see hideSchedulerAccountsCard
          above) so its empty-state banner doesn't duplicate the card. */}
      {planKnown && publishingOnly && !hideSchedulerAccountsCard && <SchedulerAccountsCard onConnect={free ? () => personalStart.start('connect') : undefined} connectDisabled={personalStart.disabled} />}

      {/* Free-tier limits notice (trial/free/payment-issue): owns its own
          fetch + null states, renders nothing for exempt/genuinely-paid users. */}
      {allowSetupWidgets && <FreeLimitsBanner />}

      {/* Autopilot paused by the monthly post limit: same self-contained
          contract; self-expires when the usage period resets (resumeAt). */}
      {allowSetupWidgets && <AutopilotLimitBanner />}

      {/* Research-first onboarding chain (flagged): renders only while the
          journey is in progress, owns its own data + null states. */}
      {allowSetupWidgets && <SetupJourneyWidget />}

      {/* MCP-first onboarding handoff: owns its data and renders only for an
          in-flight AI-assistant setup while the DARK flag is enabled. */}
      {allowSetupWidgets && <AiSetupWidget />}

      {/* TASK-296: server-state-driven "Building your setup" card — the
          universal signal that AiSetupWidget/SetupJourneyWidget above are
          NOT (both engage only off a localStorage flag written by the SPA's
          own onboarding flow, so activation that happened elsewhere — e.g.
          the ChatGPT MCP plugin — never lit them up). Never together with
          FinishSetupCard: a missing brand profile is that card's job. */}
      {allowSetupWidgets && !finishSetup && dashboardHydrated && <ActivationProgressCard />}

      {/* Autopilot panel — shown for engaged brands, firstrun Free accounts,
          and entitled no-brand accounts. Free/no-brand must take the locked
          upsell path, never the entitled-only NoBrandGuard. TASK-319: never
          for posting — an AI-generation upsell has no place on a "no AI"
          plan's own dashboard. */}
      {planKnown && !posting && ((dataReady && brandEngaged) || autopilotUpsellOnFirstrun || autopilotTrialAwaitingPayment || (autopilotEntitled && noBrand)) && <AutopilotWidget />}

      {/* The big in-progress generation widget was removed here. Batch progress
          is already surfaced by the topbar Activity pill + the floating
          GenerationStatusPlate; a third on-dashboard indicator was redundant
          (and visually heavy next to the Autopilot panel). Tap the pill/plate
          to open the live batch. */}

      {/* Owner decision 2026-09-07 (item 1): analytics is INCLUDED for posting
          (Scheduler) — the backend flag flips separately, and the gateway/
          analytics-worker were never plan-gated to begin with, so the data
          already flows. StatCards therefore renders for every plan again;
          the stale `postingPlan.excluded3` ("Analytics and autopilot") no
          longer describes this card, only the still-excluded Autopilot
          widget above. */}
      {planKnown && stats.length > 0 && <StatCards items={stats} />}

      {/* Owner decision 2026-09-07 (item 1): posting used to get its own
          full-width WeekStrip/SchedulerNextUp branch here (no AnalyticsPanel
          at all, since analytics was excluded from the plan). Analytics is
          now included, so posting renders the exact same dash-grid-2
          composition as every other plan — no posting-specific branch left.
          Fix list item 8b: still planKnown-gated so nothing flashes the
          wrong composition before entitlements resolve. */}
      {planKnown && (
        <div className="dash-grid-2">
          <AnalyticsPanel
            locked={free && !analyticsReadable}
            onUnlock={() => navigate('/app/plans')}
            rangeDays={analyticsDays}
            onRangeChange={days => {
              if (days === analyticsDays) return;
              setPostsSummary(null);
              setOverview(null);
              setAnalyticsDays(days);
            }}
            engagementData={engagementSeries}
            overviewDaily={overview?.engagement || []}
            engagementMode={engagementMode}
            reach={overview?.reach || []}
            platforms={overview?.platforms || []}
            interactions={overview?.interactions || []}
            audience={overview?.audience || []}
            bestTimes={overview?.bestTimes || null}
            periodSummary={overview?.periodSummary || null}
            partial={postsSummaryLoading || postsSummary?.partial === true || (overview?.periodSummary && overview.periodSummary.metricSnapshotTargets < overview.periodSummary.publishedTargets) || engagementSeries.some(d => d.published > 0 && d.engagement === null)}
          />
          <WeekStrip days={weekDays} />
        </div>
      )}

      {/* TASK-319: posting activity feed — last 5 content items, once any
          exist. Nothing to show pre-first-post, so it's simply absent then. */}
      {planKnown && publishingOnly && recentPosts.length > 0 && <SchedulerRecentPosts items={recentPosts} />}

      {allowSetupWidgets && scenario === 'firstrun' && !finishSetup && !posting && <FirstRunHero checklist={checklist} />}


      {scenario !== 'firstrun' && <AttentionPanel items={attentionItems} allClear={attentionItems.length === 0} researchHint={researchHint} />}

      {showTour && <DashboardTour onClose={() => setShowTour(false)} />}
    </div>
  );
};

export default Dashboard;
